import React, { useState } from 'react';
import { Box, Button, Stack, Typography, Paper, IconButton, CircularProgress, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { HttpService } from 'core/services/http';
import { CVHttpService } from 'features/feature_cv/services';
import { Utils } from 'core/utils';
import ReferenceFormDialog from 'features/feature_cv/components/dialogs/ReferenceFormDialog';

const formatText = (value, fallback = '—') => {
    if (!Utils.normalizeText) return value ?? fallback;
    return Utils.normalizeText(value, fallback);
};

function ReferencesSection({ cvId }) {
    const queryClient = useQueryClient();
    const [openDialog, setOpenDialog] = useState(false);
    const [editingReference, setEditingReference] = useState(null);

    const { data, isLoading, refetch } = useQuery(
        ['cv_references', cvId],
        async () => {
            const service = new HttpService();
            const response = await service.get(`/job/cv/reference/?cv=${cvId}`, null);
            return Array.isArray(response) ? response : (response?.results || []);
        },
        { enabled: !!cvId }
    );

    const deleteMutation = useMutation(
        (id) => CVHttpService.deleteReference(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['cv_references', cvId]);
            }
        }
    );

    const references = data || [];

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this reference?')) return;
        deleteMutation.mutate(id);
    };

    const handleSave = async () => {
        await refetch();
        setOpenDialog(false);
        setEditingReference(null);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                    Professional References ({references.length})
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingReference(null);
                        setOpenDialog(true);
                    }}
                >
                    Add Reference
                </Button>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {references.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    No references added yet.
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {references.map((ref) => (
                        <Paper key={ref.id} variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="start">
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {formatText(ref.name)}
                                    </Typography>
                                    {ref.position && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {formatText(ref.position)}
                                        </Typography>
                                    )}
                                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                                        {ref.email && (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <EmailIcon fontSize="small" color="action" />
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatText(ref.email)}
                                                </Typography>
                                            </Stack>
                                        )}
                                        {ref.phone_number && (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <PhoneIcon fontSize="small" color="action" />
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatText(ref.phone_number)}
                                                </Typography>
                                            </Stack>
                                        )}
                                        {ref.location && (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <LocationOnIcon fontSize="small" color="action" />
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatText(ref.location)}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>
                                </Box>
                                <Stack direction="row" spacing={0.5}>
                                    <IconButton color="primary"
                                        size="small"
                                        onClick={() => {
                                            setEditingReference(ref);
                                            setOpenDialog(true);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(ref.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            <ReferenceFormDialog
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    setEditingReference(null);
                }}
                onSave={handleSave}
                cvId={cvId}
                reference={editingReference}
            />
        </Paper>
    );
}

export default ReferencesSection;

